import { useMemo } from "react"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"

/**
 * Hook for fetching deployments for a specific asset
 * Uses the experience API GetAssetDeployments query for server-side filtering
 *
 * @param assetId - The ID of the asset to fetch deployments for
 * @returns Deployments data, loading state, and error
 */
export function useAssetDeployments(assetId: string | undefined) {
  // Fetch deployments for the specific asset using the experience API
  const { data, loading, error, refetch } = useQueryWithEndpoint(
    gqlTypes.GetAssetDeploymentsDocument,
    {
      variables: {
        assetId: assetId!,
        activeOnly: false,
        activeDeploymentsOnly: false,
      },
      skip: !assetId,
      fetchPolicy: "cache-first",
    },
    "primary" // Use the primary (experience API) endpoint
  )

  // Extract deployments from the response
  const assetDeployments = useMemo(() => {
    if (!data?.assetDeployments?.data || !assetId) {
      return []
    }

    return data.assetDeployments.data
  }, [data?.assetDeployments?.data, assetId])

  return {
    deployments: assetDeployments,
    loading,
    error: error?.message || null,
    refetch,
  }
}
